<template>
  <form @submit.prevent="onSubmit" ref="form" class="_attachmentsForm">

    <div class="md-autocomplete-loading" v-if="isBusy">
      <md-progress-spinner :md-diameter="40" :md-stroke="4" md-mode="indeterminate" />
    </div>

    <AttachmentsDropzone
      class="_attachmentsForm__dropzone"
      v-model="files"
      v-if="!model || !model.id"
      @addedFiles="onAddedFiles"
    />

    <div class="_attachmentsForm__files">
      <AttachmentsFormItem
        v-for="(_item, _index) in filteredAttachments"
        v-model="filteredAttachments[_index]"
        :key="_index"
        :docTypes="docTypes"
        :index="_index"
        :isDeleteDisabled="isDeleteDisabled"
      />
    </div>

    <div class="md-layout md-alignment-center-right _attachmentsForm__action">
      <md-button class="md-raised md-primary" type="submit" :disabled="isBusy || !isFormValid">
        <span>{{ model && model.id ? 'Save' : 'Upload' }}</span>
      </md-button>
    </div>

    <md-snackbar md-position="center" :md-active.sync="isSnackbarOpen" md-persistent>
      <span>{{ snackbarMsg }}</span>
    </md-snackbar>

  </form>
</template>

<script>
  import _ from 'lodash'
  import Amplify, { Auth, API, graphqlOperation, Storage } from 'aws-amplify';
  import * as mutations from '@/graphql/mutations';
  import aws_exports from "@/aws-exports";
  import AttachmentsDropzone from '@/components/DynamicForm/Attachments/AttachmentsDropzone';
  import AttachmentsFormItem from '@/components/DynamicForm/Attachments/AttachmentsFormItem';

  const uuidv1 = require('uuid/v1');
  const { v4: uuidv4 } = require('uuid');

  export default {
    name: 'AttachmentsForm',
    mixins: [],
    components: {
      AttachmentsDropzone,
      AttachmentsFormItem,
    },
    props: {
    	model: {
        type: Object,
        required: false,
      },
      docTypes: {
        type: Array,
        required: true,
      },
      relations: {
        type: Array,
        required: true,
      },
      isDeleteDisabled: {
        type: Boolean,
        default: false,
      },
    },
    data: () => ({
      files: null,
      attachments: [],
      isBusy: false,
      snackbarMsg: '',
      isSnackbarOpen: false,
    }),
    computed: {
      filteredAttachments() {
        return this.attachments.filter(_attachment => {
          if (_attachment.isDeleted) {
            return false
          }
          return !_attachment.internal || this.$Session.isInternalGroupMember
        })
      },
      isFormValid() {
        // every attachment needs to have a file attached to it
        return this.filteredAttachments.every(_attachment => {
          return (
            (_attachment.file || _attachment.id)
            && _attachment.type
          )
        })
      },
    },
    methods: {
      /**
       * Reset form
       * @return {void}
       */
      onFormReset() {
        this.attachments = []
      },
      /**
       * When new files are added update files list
       * @param  {FileList} fileList
       * @return {void}
       */
      onAddedFiles(fileList) {
        if (fileList && fileList.length) {
          for (const _file of fileList) {
            this.attachments.push({
              file: _file,
              name: _file.name,
              description: '',
            })
          }
        }
      },
      /**
       * Form submitted - add or update attachment
       * @returns {void}
       */
      async onSubmit() {
        this.isBusy = true;
        if (this.model && this.model.id) {
          return this.update();
        }
        this.add();
      },
      /**
       * Add new attachment - Upload file to AWS S3 and create new attachment & Relations
       * @param {Mixed} relations null or Array of objects holding relations
       * @returns {Void}
       */
      async add(relations) {
        let error = null;
        let s3Object = {};
        let s3Uploads = []
        
        // remove duplicate relations
        relations = _.uniqWith(relations, _.isEqual)

        // filter out all attachments that are deleted
        const deletedAttachments = this.attachments.filter(_attachment => _attachment.isDeleted)

        // get remaining attachments
        this.attachments = this.attachments.filter(_attachment => !_attachment.isDeleted)
        
        // make sure all attachments with id are in edit mode
        for (const _attachment of this.attachments) {
          // doesn't have a id yet - bail
          if (!_attachment.id) {
            continue
          }
          // is already in edit mode - bail
          if (_attachment.isEdit) {
            continue
          }
          // in some places the attachments are being displayed in the frontend
          // with _ removed from type - we need to insert it back
          _attachment.type = _attachment.type.replace(/\s/ig, '_')
          _attachment.isEdit = true
        }

        // upload files to s3
        for (const _attachment of this.attachments) {
          // skip any update attachments
          if (_attachment.isEdit && _attachment.id) {
            continue
          }

          _attachment.s3Object = await this.uploadFile(_attachment).catch(err => error = err);
          if (error) {
            return this.showNotification(null, error);
          }
        }

        // create attachment models - for any model that fails - flag it as failed
        for (const _attachment of this.attachments) {
          // skip any update attachments
          if (_attachment.isEdit && _attachment.id) {
            continue
          }

          _attachment.attachment = await this.createAttachment(_attachment, _attachment.s3Object).catch(err => error = err);
          if (error) {
            _attachment.error = error
            this.showNotification(null, error);
          }
        }

        // create attachment relations ignoring failed ones - for any model that fails - flag it as failed
        for (const _attachment of this.attachments) {
          // skip any attachments with error || update
          if (_attachment.error || (_attachment.isEdit && _attachment.id)) {
            continue
          }

          await this.createRelations(_attachment.attachment, relations).catch(err => error = err);
          if (error) {
            _attachment.error = error
            this.showNotification(null, error);
          }
        }

        // update all attachments marked for update
        for (const _attachment of this.attachments) {
          if (!_attachment.isEdit || !_attachment.id) {
            continue
          }

          await this.updateAttachment(_attachment).catch(err => error = err);
          if (error) {
            _attachment.error = error
            this.showNotification(null, error);
          }
        }

        // delete attachments
        this.deleteAttachments(deletedAttachments)

        // filter any attachments with error
        this.attachments = this.attachments.filter(_attachment => _attachment.error)

        // if there are any attachments with errors then notify user
        if (this.attachments.length) {
          return this.showNotification()
        }

        // all files uploaded successfully
        // this.onComplete('File Uploaded!');
        
        this.$emit('success');
        this.isBusy = false;
      },
      /**
       * Update attachment
       * @returns {Void}
       */
      async update() {
        let error = null;
        await this.updateAttachment().catch(err => error = err);
        if (error) {
          return this.showNotification(null, error);
        }
        this.onComplete('File Updated!');
      },
      /**
       * Create or update attachment
       * @param  {Object} attachment Object with file, type, description and isInternal properties
       * @param  {Object} s3Object Object containing S3Object information
       * @return {Promise}
       */
      createAttachment(attachment, s3Object) {
        let input = {
          name: s3Object.name,
          type: attachment.type,
          internal: attachment.internal ? true : false,
          description: attachment.description,
          s3Path: s3Object ? s3Object.Key: '',
          attachmentUpdatedById: this.$reduce(this, '$Session.userId'),
        };
        const operation = graphqlOperation(mutations.createAttachment, { input });
        return API.graphql(operation)
      },
      /**
       * Update attachment
       * @returns {Promise}
       */
      updateAttachment(model) {
        let attachment = model
        if (!model && this.attachments && this.attachments[0]) {
          attachment = this.attachments[0]
        }

        let input = {
          id: attachment.id,
          type: attachment.type,
          internal: attachment.internal,
          description: attachment.description,
          attachmentUpdatedById: this.$reduce(this, '$Session.userId'),
        };

        const operation = graphqlOperation(mutations.updateAttachment, { input });
        return API.graphql(operation)
      },
      /**
       * Create attachment relations
       * @param {Object} attachment
       * @param {Mixed} relations
       * @return Promise
       */
      createRelations(attachment, relations) {
        let promises = [];
        let query = mutations.createRelation;

        const attachmentId = this.$reduce(attachment, 'data.createAttachment.id');
        if (!attachmentId) {
          return Promise.resolve();
        }

        let relationsToAdd = relations
        if (!relationsToAdd || !relationsToAdd.length) {
          relationsToAdd = this.relations
        }

        for (const _relation of relationsToAdd) {
          const input = Object.assign({}, _relation, {
            toModelType: 'Attachment',
            relationAttachmentId: attachmentId,
            relationUpdatedById: this.$reduce(this, '$Session.userId'),
          });
          const operation = graphqlOperation(query, { input });
          promises.push(API.graphql(operation));
        }

        return Promise.allSettled(promises);
      },
      /**
       * Clean up and show notification
       * @param  {String} msg Message to display
       * @returns {void}
       */
      onComplete(msg) {
        this.showNotification(msg);
        this.isBusy = false;
      },
      /**
       * Upload selected file to AWSS3
       * @param  {Object} attachment Object with file, type, description and isInternal properties
       * @return {Promise} Object containing upload info or error object
       */
      async uploadFile(attachment) {
        return new Promise(async (resolve, reject) => {
          // let error = null;
          // const dir = 'attachments/comments';
          // const uuid = uuidv1();
          // const amplifyEnv = aws_exports.aws_content_delivery_bucket.match(/.*-(\w+)/)[1]
          // const s3Bucket = 'atlasemailattachments-' + amplifyEnv
          // const s3FileKey = `${dir}/${uuid}/${attachment.file.name}`;
          // const data = {
          //   Bucket: s3Bucket,
          //   Key: s3FileKey,
          //   Body: attachment.file,
          //   ACL: "public-read"
          // };

          // console.log('uploadFile - data - ', data)
          // const credentials = await this.getAwsCredentials().catch(err => error = err);
          // if (error) {
          //   return reject(error);
          // }

          // const s3Object = await this.uploadToAwsS3(credentials, data).catch(err => error = err);
          // if (error) {
          //   return reject(error);
          // }
          
          let error = null;
          const s3Key = `comments/${uuidv4()}`;
          const s3Response = await Storage
            .put(s3Key, attachment.file, {
              bucket: aws_exports.aws_user_files_s3_bucket,
              region: aws_exports.aws_user_files_s3_bucket_region,
              contentType: attachment.file.type,
              contentDisposition: 'inline;filename="'+attachment.file.name+'"'
            })
            .catch(err => {
              error = err;
              console.log('Error uploading comment to S3', err)
            });
          
          if (error) {
            return reject(error);
          }

          return resolve({
            Key: s3Key,
            name: attachment.file.name,
          });
        });
      },
      /**
       * Get current logged in users AWS credentials
       * @return {Promise} AWSCredentials or error object
       */
      async getAwsCredentials() {
        return new Promise(async (resolve, reject) => {
          let error = null;

          const user = await Auth.currentAuthenticatedUser().catch(err => error = err);
          if (error) {
            console.log('getAwsCredentials - Error', error);
            return reject(error);
          }

          const credentials = await Auth.currentCredentials().catch(err => error = err);
          if (error) {
            console.log('getAwsCredentials - Error', error);
            return reject(error);
          }

          return resolve(credentials);
        });
      },
      /**
       * Upload an object to AWS S3
       * @param  {credentials} AWS credentials object
       * @param  {Object} data AWS S3 storage object
       * @return {Promise}
       */
      async uploadToAwsS3(credentials, data) {
        const S3 = new AWS.S3({
          apiVersion: '2006-03-01',
          credentials: Auth.essentialCredentials(credentials),
          region: aws_exports.aws_appsync_region
        });
        return S3.putObject(data).promise();
      },
      /**
       * show notification messge
       * if error object provided found then show error notification
       * @param  {String} msg the notificaiton message
       * @param  {Object} error the error object
       * @return {void}
       */
      showNotification(msg, error) {
        console.log(msg);
        console.log(error);
        this.isBusy = false;

        if (msg) {
          this.snackbarMsg = msg;
          this.isSnackbarOpen = true;
          return;
        }

        if (error && error.message) {
          this.snackbarMsg = error.message;
          this.isSnackbarOpen = true;
          return;
        }

        this.snackbarMsg = 'Something went wrong - please try again!';
        this.isSnackbarOpen = true;
      },
      /**
       * Delete multiple attachments
       * @param  {Array} attachments array of attachment objects
       * @return {Void}
       */
      async deleteAttachments(attachments) {
        const promises = []
        const mutation = mutations.deleteAttachment;
        
        for (const _attachment of attachments) {
          // skip any attachment without id
          if (!_attachment.id) {
            continue
          }
          
          // payload
          const input = { id: _attachment.id }
          const operation = graphqlOperation(mutation, { input })
          promises.push(API.graphql(operation))
        }
        
        Promise.allSettled(promises)
      }
    },
    watch: {
      /**
       * Watch for changes to model - the attachment relation object passed to
       * form - contains the attachment as a prop
       * Update internal state to reflect attachment
       */
      model: {
        immediate: true,
        handler: function() {
          const attachment = this.$reduce(this, 'model.attachment');
          if (!attachment || !attachment.id) {
            return;
          }
          this.attachments = [attachment]
        }
      },
    }
  };
</script>

<style lang="scss" scoped>
</style>
